import React, { useState, useEffect } from 'react'
import { Plus, Pencil, Trash2, Check, X, AlertCircle } from 'lucide-react'
import { bidWorkupService } from '../../../shared/api'

export default function ScheduleSettings() {
  const [presets, setPresets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState({ name: '', days_per_week: 5, hours_per_day: 10 })

  const fetchPresets = async () => {
    try {
      setLoading(true)
      const data = await bidWorkupService.getSchedulePresets()
      setPresets(data.presets)
      setError(null)
    } catch (err) {
      console.error(err)
      setError('Failed to load schedule presets')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPresets()
  }, [])

  const handleEdit = (preset) => {
    setEditingId(preset.id)
    setEditForm({
      name: preset.name,
      days_per_week: preset.days_per_week,
      hours_per_day: preset.hours_per_day
    })
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditForm({ name: '', days_per_week: 5, hours_per_day: 10 })
  }

  const handleSave = async () => {
    try {
      setError(null)
      if (editingId === 'new') {
        await bidWorkupService.createSchedulePreset(editForm)
      } else {
        await bidWorkupService.updateSchedulePreset(editingId, editForm)
      }
      await fetchPresets()
      handleCancel()
    } catch (err) {
      console.error(err)
      setError(err.response?.data?.detail || 'Failed to save preset')
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this preset?')) return
    try {
      await bidWorkupService.deleteSchedulePreset(id)
      await fetchPresets()
    } catch (err) {
      console.error(err)
      setError('Failed to delete preset')
    }
  }

  const handleAddNew = () => {
    setEditingId('new')
    setEditForm({ name: '', days_per_week: 5, hours_per_day: 10 })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Schedule Presets</h2>
          <p className="text-sm text-gray-500">
            Configure the default dropdown options for bid schedules.
          </p>
        </div>
        <button
          onClick={handleAddNew}
          disabled={editingId !== null}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-bfs-maroon hover:bg-bfs-maroon-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bfs-maroon disabled:opacity-50"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Preset
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {loading && presets.length === 0 ? (
            <li className="px-4 py-4 text-center text-sm text-gray-500">Loading...</li>
          ) : (
            presets.map((preset) => (
              <li key={preset.id} className="px-4 py-4 sm:px-6">
                {editingId === preset.id ? (
                  <div className="flex items-center space-x-4">
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Preset Name</label>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-bfs-maroon focus:border-bfs-maroon sm:text-sm"
                          placeholder="e.g. 5 - 10s"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Days / Week</label>
                        <input
                          type="number"
                          min="1" max="7"
                          value={editForm.days_per_week}
                          onChange={(e) => setEditForm({ ...editForm, days_per_week: parseInt(e.target.value) || 0 })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-bfs-maroon focus:border-bfs-maroon sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">Hours / Day</label>
                        <input
                          type="number"
                          step="0.5" min="1" max="24"
                          value={editForm.hours_per_day}
                          onChange={(e) => setEditForm({ ...editForm, hours_per_day: parseFloat(e.target.value) || 0 })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-bfs-maroon focus:border-bfs-maroon sm:text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 pt-5">
                      <button onClick={handleSave} className="p-1 text-green-600 hover:bg-green-50 rounded">
                        <Check className="h-5 w-5" />
                      </button>
                      <button onClick={handleCancel} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-bfs-maroon truncate">
                        {preset.name}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {preset.days_per_week} Days / Week • {preset.hours_per_day} Hrs / Day
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(preset)}
                        disabled={editingId !== null}
                        className="p-1 text-gray-400 hover:text-bfs-maroon disabled:opacity-50"
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(preset.id)}
                        disabled={editingId !== null}
                        className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))
          )}
          
          {editingId === 'new' && (
            <li className="px-4 py-4 sm:px-6 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center space-x-4">
                <div className="flex-1 grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700">Preset Name</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-bfs-maroon focus:border-bfs-maroon sm:text-sm"
                      placeholder="e.g. 5 - 10s"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">Days / Week</label>
                    <input
                      type="number"
                      min="1" max="7"
                      value={editForm.days_per_week}
                      onChange={(e) => setEditForm({ ...editForm, days_per_week: parseInt(e.target.value) || 0 })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-bfs-maroon focus:border-bfs-maroon sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700">Hours / Day</label>
                    <input
                      type="number"
                      step="0.5" min="1" max="24"
                      value={editForm.hours_per_day}
                      onChange={(e) => setEditForm({ ...editForm, hours_per_day: parseFloat(e.target.value) || 0 })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-bfs-maroon focus:border-bfs-maroon sm:text-sm"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2 pt-5">
                  <button onClick={handleSave} className="p-1 text-green-600 hover:bg-green-50 rounded" disabled={!editForm.name}>
                    <Check className="h-5 w-5" />
                  </button>
                  <button onClick={handleCancel} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </li>
          )}
        </ul>
      </div>
    </div>
  )
}
